import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { downloadFile } from '@/features/admin-v2/download';
import { api } from '@/lib/api';

export type LocumPayStatus = 'running' | 'submitted' | 'approved' | 'paid';

export interface LocumPayWeek {
  id: string;
  locumUserId: string;
  locumName: string;
  branchId: string;
  branchName: string;
  weekStartsOn: string;
  weekEndsOn: string;
  attendanceMinutes: number;
  hourlyRateSen: number;
  grossPaySen: number;
  correctionRequired: boolean;
  correctionRecordIds: string[];
  status: LocumPayStatus;
  managerNote: string | null;
  approvedAt: string | null;
  /** RL-1808: who approved and who paid (API #218). */
  approvedByName?: string | null;
  paidAt: string | null;
  paidByName?: string | null;
  bankReference: string | null;
}

export interface ApproveLocumPayInput {
  id: string;
}

export interface ProcessLocumPaymentInput {
  id: string;
  bankReference: string;
}

export interface PayrollFeedExport {
  id: string;
  fileName: string;
  rowCount: number;
  exportedAt: string;
}

export const locumPayKeys = {
  all: ['locum-pay'] as const,
  mine: ['locum-pay', 'mine'] as const,
  manager: ['locum-pay', 'manager'] as const,
  finance: ['locum-pay', 'finance'] as const,
};

export function useMyLocumPay() {
  return useQuery({
    queryKey: locumPayKeys.mine,
    queryFn: ({ signal }) =>
      api
        .get<LocumPayWeek[]>('/locum-pay/me', { signal })
        .then((response) => response.data),
  });
}

export function useManagerLocumPay() {
  return useQuery({
    queryKey: locumPayKeys.manager,
    queryFn: ({ signal }) =>
      api
        .get<LocumPayWeek[]>('/admin/locum-pay', { signal })
        .then((response) => response.data),
  });
}

export function useApproveLocumPay() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: ApproveLocumPayInput) =>
      api
        .patch<LocumPayWeek>(`/admin/locum-pay/${id}/decision`, {
          decision: 'approved',
        })
        .then((response) => response.data),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: locumPayKeys.all }),
  });
}

export function useFinanceLocumPayouts() {
  return useQuery({
    queryKey: locumPayKeys.finance,
    queryFn: ({ signal }) =>
      api
        .get<LocumPayWeek[]>('/finance/locum-pay', { signal })
        .then((response) => response.data),
  });
}

export function useProcessLocumPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, bankReference }: ProcessLocumPaymentInput) =>
      api
        .post<LocumPayWeek>(`/finance/locum-pay/${id}/pay`, {
          bankReference,
        })
        .then((response) => response.data),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: locumPayKeys.all }),
  });
}

export function useExportLocumPayrollFeed() {
  return useMutation({
    // The API stores the export; the CSV comes from GET exports/{id}.
    mutationFn: async () => {
      const { data } = await api.post<PayrollFeedExport>(
        '/finance/locum-pay/books-export',
      );
      await downloadFile(
        `/finance/locum-pay/exports/${data.id}`,
        data.fileName,
      );
      return data;
    },
  });
}
